import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { ProductTour, type TourStep } from '../lib/ProductTour';

/**
 * Main menu — mirrors legacy `resources/views/home.blade.php`.
 *
 * Layout: full-width 12-col Bootstrap row of `mainmenubuttons` tiles.
 * Each tile uses the legacy `<x-menubutton>` markup (image left, label
 * center, info icon right; bordered 3-px grey card; link-dark anchor).
 *
 * Guests see a reduced set (Configure New Structure + Explore More Tools).
 */
// Mirrors the legacy intro.js `stepsconfig` in home.blade.php.
const TOUR_STEPS: TourStep[] = [
  {
    selector: '[data-tour="step1"]',
    intro: '<b>Configure New Structure</b>: Start by configuring your first structure.',
  },
  {
    selector: '[data-tour="step2"]',
    intro: '<b>View Structures</b>: Review or make changes to previous structure configurations here.',
  },
  {
    selector: '[data-tour="step3"]',
    intro: '<b>Create New Project Folder</b>: Once your structures are configured, create a project folder. Add multiple structures and define quantities to send to ADS for quote.',
  },
  {
    selector: '[data-tour="step4"]',
    intro: '<b>View Project Folders</b>: Review, requote or make changes to previous projects here.',
  },
  {
    selector: '[data-tour="step5"]',
    intro: '<b>Help + Reference Icon</b>: If you need more information about something, click on these help icons.',
  },
  {
    selector: '[data-tour="tour-link"]',
    intro: 'You can replay this tour at any time by clicking here.',
  },
];

// The legacy `hometour` flag lives on the user record (server-side), so the
// main-menu tour auto-shows exactly once per account on first sign-in — not
// once per browser. See AuthController.MarkHomeTourShown.

export function MenuPage() {
  const { user, setUser } = useAuth();
  const isGuest = !!user?.wasGuest;
  const isAdmin =
    !!user?.roles?.includes('admin') || !!user?.roles?.includes('super-admin');

  const [tourRunning, setTourRunning] = useState(false);

  // Auto-start the tour on first sign-in for registered (non-guest) users, then
  // persist the flag so it never auto-shows again on any device.
  useEffect(() => {
    if (!user || isGuest || user.homeTour) return;
    setTourRunning(true);
    setUser({ ...user, homeTour: true });
    api.post('/api/v1/auth/hometour').catch(() => { /* non-fatal */ });
  }, [user, isGuest, setUser]);

  return (
    <div className="container-fluid">
      <div className="row mt-1 mt-lg-3">
        <div className="col col-xl-12 col-lg-12">
          <div className="row">
            <div className="col-lg-1" />
            <div className="col-12 col-lg-10">
              <h1 className="text-center d-none d-lg-block">Main Menu</h1>
            </div>
            <div className="col-12 col-lg-1 d-flex align-items-center">
              {!isGuest && (
                <a
                  href="#"
                  data-tour="tour-link"
                  className="link-success text-nowrap"
                  onClick={(e) => {
                    e.preventDefault();
                    setTourRunning(true);
                  }}
                >
                  Tour{' '}
                  <img
                    src="/images/ICON/Icon_Question_Filled_Green.png"
                    alt=""
                    className="d-inline-block align-middle"
                    onError={(ev) => {
                      (ev.currentTarget as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="row" data-menutooltips="">
        <MenuButton
          to="/newstructure"
          image1="Icon_Plus_Green_Square"
          label="Configure New Structure"
          tooltip="Create a new structure."
          tourId="step1"
        />

        {!isGuest && (
          <>
            <MenuButton
              to="/admin/structures"
              image1="Icon_Eye"
              label="View Past Structures"
              tooltip="View past structures that you have created."
              tourId="step2"
            />
            <MenuButton
              to="/newprojectfolder"
              image1="Icon_Folder_Plus_Green"
              label="Create New Project Folder"
              tooltip="Create new project folder."
              tourId="step3"
            />
            <MenuButton
              to="/admin/projects"
              image1="Icon_Eye"
              label="View Project Folders"
              tooltip="View project folders."
              tourId="step4"
            />
            <MenuButton
              to="/admin/account"
              image1="Icon_Gear"
              label="Account Settings"
              tooltip="Change the info you gave ADS when you first started your account."
            />
            <MenuButton
              to="/help"
              image1="Icon_Info_Green_Square"
              label="Help + Reference"
              tooltip="Help + Reference"
              tourId="step5"
            />
            {isAdmin && (
              <MenuButton
                to="/admin/console#users-report"
                image1="Icon_Report"
                label="View Metrics Dashboard"
                tooltip="View site metrics."
              />
            )}
            {isAdmin && (
              <MenuButton
                to="/admin/foundries"
                image1="Icon_Gear"
                label="Edit Foundry and Precast Parts"
                tooltip="Edit foundries and their precast parts."
              />
            )}
          </>
        )}

        <div className="full-width-button col-12 px-lg-2 px-1 py-0">
          <MenuButton
            externalHref="https://www.adspipe.com/resources/ads-design-studio"
            image1="Icon_Explore"
            label="Explore More Tools in the ADS Design Studio"
            size={12}
            tooltip="Browse the ADS Design Studio."
          />
        </div>
      </div>

      <ProductTour
        steps={TOUR_STEPS}
        run={tourRunning}
        onClose={() => setTourRunning(false)}
      />
    </div>
  );
}

interface MenuButtonProps {
  to?: string;
  externalHref?: string;
  image1: string;
  label: ReactNode;
  tooltip?: string;
  size?: 6 | 12;
  tourId?: string;
}

function MenuButton({
  to,
  externalHref,
  image1,
  label,
  tooltip,
  size = 6,
  tourId,
}: MenuButtonProps) {
  const colClass = `col col-xl-${size} col-lg-${size} col-12 px-lg-2 py-lg-2 px-1 py-1`;

  const inner = (
    <div className="row align-middle p-1 ps-2 border border-grey border-3 g-0 d-flex align-items-center mainmenubuttons">
      <div className="col-auto pe-0 pe-lg-2">
        <img
          className="img-fluid"
          src={`/images/ICON/MainMenu/${image1}.png`}
          alt=""
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).style.display = 'none';
          }}
        />
      </div>
      <div className="col-lg-10 col-10 align-middle d-block d-lg-none">
        <div className="dark ps-1" style={{ display: 'inline-block' }}>
          {label}
        </div>
      </div>
      <div className="col-lg-10 col-10 align-middle d-none d-lg-block">
        <div className="dark fs-2" style={{ display: 'inline' }}>
          {label}
        </div>
      </div>
      {tooltip && (
        <div className="col-auto ms-auto d-lg-flex d-none">
          <img
            className="img-fluid"
            src="/assets/images/Icon_Info.png"
            alt=""
            title={tooltip}
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = 'none';
            }}
          />
        </div>
      )}
    </div>
  );

  if (externalHref) {
    return (
      <div className={colClass} data-tour={tourId}>
        <a
          href={externalHref}
          target="_blank"
          rel="noreferrer"
          className="link-dark platformtraffic"
        >
          {inner}
        </a>
      </div>
    );
  }

  return (
    <div className={colClass} data-tour={tourId}>
      <Link to={to ?? '#'} className="link-dark platformtraffic">
        {inner}
      </Link>
    </div>
  );
}
